import React, { useEffect, useState, useMemo, forwardRef, useRef, useImperativeHandle, useCallback } from 'react'
import clsx from 'clsx'
import { makeStyles } from '@mui/styles'
import { Form, Formik } from 'formik'
import lang from 'lang'
import { Title } from '@/modules/base/components/texts'
import ThemeInterface from '@/theme-interface'
import { IbanInput, SelectInput, TextInput } from '@/modules/base/components/inputs'
import VatNumberInput from '@/modules/customer/components/edit/forms/information/vat-number-input'
import { fetchCompanyData, submitCompanyData } from '@/redux/ducks/settings/company'
import config from '@/config'
import request from '@/modules/base/util/request'
import useClientSetting from '@/modules/client/_helpers/useClientSetting'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import IconInfo from '@assets/images/icons/info-icon.svg'
import TenantBankAccountService, { TenantBankAccountData } from '@/modules/tenantBankAccount/tenantBankAccountService'
import * as Yup from 'yup'
import { toast } from '../../../toast/toastConfig'
import { USER_RIGHTS } from '@/modules/base/util/constants'
import useCheckAccessRights from '@/modules/_helpers/accessCheck/useCheckAccessRights'
import { useAppDispatch, useAppSelector } from '@/redux/store'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  title: {
    textAlign: 'left',
    fontWeight: theme.fontWeights.medium,
    fontSize: theme.fontSizes.smedium,
    display: 'flex',
    alignItems: 'center',
    gap: 5,
  },
  wrapper: {
    paddingTop: 20,
  },
  row: {
    display: 'flex',
    padding: '5px 0px',
    '& > *:first-child': {
      paddingRight: 5,
    },
    '& > *:last-child': {
      paddingLeft: 5,
    },
    '& > *:first-child:last-child': {
      padding: 0,
    },
  },
  half: {
    width: '50%',
  },
  vatNumberInput: {
    width: '100%',
  },
  inputState: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    paddingRight: 25,
  },
  inputOffice: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: 25,
  },
}))

const taxOfficeUrl = `${config.resourceHost}taxoffice`
const initTaxOfficeState = { contactList: [], addresseList: [] }
const dataQsIdPrefix = 'onboardingTesterModal-bankingStep'

const validationSchema = Yup.object().shape({
  taxNumber: Yup.string(),
  taxOfficeId: Yup.string(),
  vatNumber: Yup.string(),
  accountHolder: Yup.string(),
  iban: Yup.string(),
  bic: Yup.string(),
})

const BankingData = forwardRef(({ onSubmitted, onValid }, ref) => {
  const classes = useStyles()
  const dispatch = useAppDispatch()
  const readOnly = !useCheckAccessRights(USER_RIGHTS.SETTINGS.WRITE)
  const { client } = useClientSetting()
  const [allStates, setAllStates] = useState<Record<string, any>>({})
  const [taxOfficeState, setTaxOfficeState] = useState<string | null>(null)
  const [taxOffice, setTaxOffice] = useState<any>(initTaxOfficeState)
  const [taxNumberOfficeInvalid, setTaxNumberOfficeInvalid] = useState<boolean>(false)
  const [isIbanValid, setIsIbanValid] = useState(true)
  const companyData = useAppSelector((state) => state.settings.company)
  const userInfo = useAppSelector((state) => state.user.info)
  // re-enable when update billingData not applies SEPA mandate automatically anymore
  // const { subscriptions, selectedSubscriptionId } = useSelector(state => state.subscription.details)

  const [intialBankAccount, setIntialBankAccount] = useState<TenantBankAccountData>(null)
  // re-enable when update billingData not applies SEPA mandate automatically anymore
  // const sub = subscriptions.find(subscription => subscription.id === selectedSubscriptionId)

  const initialValues = {
    taxNumber: companyData?.taxNumber || '',
    taxOfficeId: companyData?.taxOfficeId || '',
    vatNumber: companyData?.vatNumber || '',
    accountHolder: intialBankAccount?.accountHolder || `${userInfo.firstName} ${userInfo.lastName}`.trim() || '',
    iban: intialBankAccount?.iban || '',
    // re-enable when update billingData not applies SEPA mandate automatically anymore
    // accountHolder: intialBankAccount?.accountHolder || sub?.billingData?.bankAccountHolder || `${userInfo.firstName} ${userInfo.lastName}`.trim() || '',
    // iban: intialBankAccount?.iban || sub?.billingData?.bankAccountIBAN || '',
    bic: intialBankAccount?.bic || '',
  }

  const formikRef = useRef(null)
  useImperativeHandle(ref, () => ({
    save: () => {
      formikRef.current?.handleSubmit()
    },
  }))

  useEffect(() => {
    TenantBankAccountService.getDefault().then(bankAccount => {
      setIntialBankAccount(bankAccount)
      setIsIbanValid(!!bankAccount)
    })
    dispatch(fetchCompanyData(client?.id))
    loadAllStatesWithTaxOffices()
  }, [dispatch, client?.id])

  const loadAllStatesWithTaxOffices = () => {
    request(`${taxOfficeUrl}/states`, { auth: true, isConcurrent: true })
      .then(({ body: { data } }) => setAllStates(data))
      .done()
  }

  const findOfficeState = (bufaNr: string) => {
    const statesArr = Object.values(allStates)
    return statesArr.flat().find((taxOfficeItem: any) => taxOfficeItem.buFaNr === bufaNr)
  }

  const validateTaxSettings = async(values: any, fieldName: string, value: string) => {
    try {
      const { taxOfficeId, taxNumber } = { ...values, [`${fieldName}`]: value }
      if (!taxOfficeId || !taxNumber) {
        setTaxNumberOfficeInvalid(false)
        return false
      }

      const url = `${config.resourceHost}vatReturn/validateTaxSetting`
      const headers: Record<string, string> = {}
      if (client?.id) headers['collaboration-id'] = client?.id

      const response = await request(url, {
        auth: true,
        isConcurrent: true,
        method: 'POST',
        data: { bufa: taxOfficeId, taxNumber },
        headers,
      })
      const { body: { data: { isValid } } } = response
      setTaxNumberOfficeInvalid(!isValid)
    } catch (e) {
      return false
    }
  }

  const changeTaxOfficeState = (newTaxOffice: any, values: any) => {
    setTaxOffice(newTaxOffice)
    setTaxOfficeState(newTaxOffice.state)
    validateTaxSettings(values, 'taxOfficeId', newTaxOffice.buFaNr)
  }

  const onStateChange = (option: { value: string }) => {
    setTaxOfficeState(option.value)
  }

  const handleTaxOfficeChange = (
    option: { value: string },
    values,
  ) => {
    const foundTaxOffice = findOfficeState(option.value)
    if (foundTaxOffice) {
      changeTaxOfficeState(foundTaxOffice, values)
    } else {
      setTaxOffice(initTaxOfficeState)
    }
  }

  const onBlurTaxNumber = async(
    name: string,
    value: string,
    formValues: any,
    setValue: (field: string, val: any) => void,
  ) => {
    if (taxOffice?.name) {
      return validateTaxSettings(formValues, name, value)
    }
    if (taxOfficeState) {
      const shortValue = value.substring(0, 2)
      const longValue = value.substring(0, 3)
      const taxOfficesFromState = allStates[taxOfficeState]
      if (!taxOfficesFromState) return

      const buFaNr
        = taxOfficesFromState.find((st: any) => st.buFaNr.endsWith(longValue))?.buFaNr
        || taxOfficesFromState.find((st: any) => st.buFaNr.endsWith(shortValue))?.buFaNr

      if (buFaNr) {
        setValue('taxOfficeId', buFaNr)
        validateTaxSettings({ ...formValues, taxOfficeId: buFaNr }, name, value)
      }
    }
  }

  const onUpdateValidation = useCallback((isValied: boolean) => {
    onValid(isValied && isIbanValid)
  }, [onValid, isIbanValid])

  const onIbanValidation = (ibanIsValid: boolean, bankData: { bic?: string, iban?: string, bankname?: string}, formikHelper) => {
    if (!ibanIsValid && bankData?.iban) {
      formikHelper.setFieldValue('bic', '')
      formikHelper.setFieldTouched('iban', true, false)
      setIsIbanValid(false)
      return
    }
    formikHelper.setFieldValue('bic', bankData?.bic || '')
    formikHelper.setFieldValue('bankname', bankData?.bankname || '')
    setIsIbanValid(true)
  }

  const getStateSelectOptions = () => {
    const stateOptions = Object.keys(allStates)
      .sort()
      .map((item) => ({ value: item, label: item }))

    return {
      label: lang.tax.federalState,
      name: 'states',
      clearable: false,
      allowCreate: false,
      notAsync: true,
      customSelectOptions: {
        valueKey: 'value',
        labelKey: 'label',
        defaultOptions: true,
        menuPlacement: 'top',
      },
      options: stateOptions,
      value: taxOfficeState,
      handleChange: onStateChange,
      readOnly,
      useFormik: false,
    }
  }

  const taxOfficeOptions = useMemo(() => {
    const taxOfficesFromState = allStates[taxOfficeState] || []
    return taxOfficesFromState.map((office: any) => ({
      label: `${office.name} ${office.buFaNr}`,
      value: office.buFaNr,
    }))
  }, [taxOfficeState, allStates])

  const onSubmitHandle = async(values) => {
    const bankAccount = {
      iban: values.iban,
      bic: values.bic,
      accountHolder: values.accountHolder,
    }
    if (isIbanValid && bankAccount.iban && bankAccount.accountHolder) {
      if (intialBankAccount?.id) {
        await TenantBankAccountService.update(intialBankAccount.id, bankAccount)
          .catch(error => {
            toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
            throw error
          })
      } else {
        await TenantBankAccountService.createDefault(bankAccount)
          .catch(error => {
            toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
            throw error
          })
      }
      // re-enable when update billingData not applies SEPA mandate automatically anymore
      // if (!sub?.billingData?.bankAccountHolder) {
      //   request(billingDataConfig.urls.update(selectedSubscriptionId),
      //     {
      //       auth: true,
      //       method: 'PUT',
      //       data: {
      //         bankAccountHolder: bankAccount.accountHolder,
      //         bankAccountIBAN: bankAccount.iban,
      //         bankAccountBic: bankAccount.bic,
      //       },
      //     },
      //   )
      // }
    }
    dispatch(submitCompanyData({
      ...companyData,
      taxNumber: values.taxNumber,
      taxOfficeId: values.taxOfficeId,
      vatNumber: values.vatNumber,
    }))
    onSubmitted()
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validationSchema= {validationSchema }
      innerRef={formikRef}
      onSubmit={(values) => {
        onSubmitHandle(values)
      }}
      validateOnMount
    >
      {({ values, isValid, isSubmitting, ...formikHelpers }) => {
        onUpdateValidation(isValid && !isSubmitting)
        // Runs on every re-render if values change
        useEffect(() => {
          if (!values.taxOfficeId) return
          const foundTaxOffice = findOfficeState(values.taxOfficeId)
          if (foundTaxOffice) {
            changeTaxOfficeState(foundTaxOffice, values)
          }
        }, [values.taxOfficeId, allStates])

        useEffect(() => {
          if (values.taxNumber) {
            validateTaxSettings(values, 'taxNumber', values.taxNumber)
          }
        }, [values.taxNumber])

        return (
          <Form>
            <div className={classes.wrapper}>
              <Title className={classes.title}>
                {lang.onboardingTesterData.bankAccount}
                <RichTooltip
                  body={lang.onboardingTesterData.bankingTooltip}
                  width="500px"
                  placement="right"
                >
                  <SVGIcon width={14} height={14} svg={IconInfo} />
                </RichTooltip>
              </Title>

              <div className={clsx(classes.row)}>
                <div className={classes.half}>
                  <TextInput
                    dataQsId={`${dataQsIdPrefix}-accountHolderInput`}
                    name="accountHolder"
                    label="Kontoinhaber"
                    useFormik
                    readOnly={readOnly}
                  />
                </div>
                <div className={classes.half}>
                  <IbanInput
                    dataQsId={`${dataQsIdPrefix}-ibanInput`}
                    name="iban"
                    label="IBAN"
                    showCreditInstitution
                    readOnly={readOnly}
                    useFormik
                    handleValidation={(ibanIsValid, bankData) => onIbanValidation(ibanIsValid, bankData || { iban: values.iban }, formikHelpers)}
                  />
                </div>
              </div>

              <Title className={classes.title}>
                {lang.onboardingTesterData.taxTitle}
                <RichTooltip
                  body={lang.onboardingTesterData.taxTooltip}
                  width="500px"
                  placement="right"
                >
                  <SVGIcon width={14} height={14} svg={IconInfo} />
                </RichTooltip>
              </Title>

              <div className={classes.row}>
                <TextInput
                  dataQsId={`${dataQsIdPrefix}-taxNumberInput`}
                  name="taxNumber"
                  label={lang.taxNumberLabel}
                  useFormik
                  readOnly={readOnly}
                  placeholder={lang.missingCompanyData.placeholder}
                  onBlur={(e) =>
                    onBlurTaxNumber('taxNumber', e.target.value, values, formikHelpers.setFieldValue)
                  }
                />

                <div className={classes.vatNumberInput}>
                  <VatNumberInput
                    showValidationButton={false}
                    customClass={classes.vatNumberInput}
                    useFormik
                    readOnly={readOnly}
                    client={client}
                    placeholder={lang.missingCompanyData.placeholder}
                  />
                </div>
              </div>

              <div className={classes.row}>
                <div className={classes.inputState}>
                  <SelectInput {...getStateSelectOptions()} />
                </div>
                <div className={classes.inputOffice}>
                  <SelectInput
                    name="taxOfficeId"
                    label={lang.tax.taxOffice}
                    notAsync
                    customSelectOptions={{ defaultOptions: true, menuPlacement: 'top' }}
                    options={taxOfficeOptions}
                    handleChange={(option) => handleTaxOfficeChange(option, values)}
                    readOnly={readOnly}
                    useFormik
                  />
                </div>
              </div>
              {taxNumberOfficeInvalid && (
                <p style={{ color: 'red', marginTop: 8 }}>
                  {lang.tax.taxNumberOfficeInvalidHint}
                </p>
              )}
            </div>
          </Form>
        )
      }
      }
    </Formik>
  )
})

export default BankingData
